// Indicador "Nuevo" del personal: regla única del frontend.
//
// El backend ya calcula un campo `es_nuevo` (zc_backend/lib/personal-nuevo.js),
// pero NO sirve por sí solo como criterio de la UI:
//
//   es_nuevo = forzar_nuevo || (dias >= 0 && dias <= DIAS_NUEVO)
//
// El `||` cortocircuita el vencimiento, así que cualquiera con el override
// manual `forzar_nuevo = true` vuelve marcado como nuevo para siempre (se vieron
// personas con 100+ días saliendo en el dashboard). Además el umbral del backend
// es 31 días, no 30.
//
// Por eso el front reaplica su propio corte sobre la fecha real. Es duplicación
// deliberada de una regla del backend: no la quites "para no repetir lógica".
export const DIAS_NUEVO = 30;

// Medianoche de hoy, para comparar fechas sin arrastrar la hora.
const hoyMidnight = (): Date => {
  const ahora = new Date();
  return new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
};

// Fecha de inicio relevante de la persona.
// Prioridad: fecha_inicio_contrato → fecha_ingreso → created_at, que es el mismo
// orden que usa el backend (COALESCE(fecha_inicio_contrato, created_at)).
// Último recurso: reconstruirla desde dias_desde_ingreso.
export const fechaIngresoPersona = (p: any): Date | null => {
  const fecha = p?.fecha_inicio_contrato || p?.fecha_ingreso || p?.created_at || p?.fecha_creacion;
  if (typeof fecha === 'string' && fecha.trim()) {
    const ingreso = new Date(`${fecha.slice(0, 10)}T00:00:00`);
    if (!isNaN(ingreso.getTime())) return ingreso;
  }
  if (fecha instanceof Date && !isNaN(fecha.getTime())) {
    return new Date(fecha.getFullYear(), fecha.getMonth(), fecha.getDate());
  }
  if (typeof p?.dias_desde_ingreso === 'number') {
    return new Date(hoyMidnight().getTime() - p.dias_desde_ingreso * 86_400_000);
  }
  return null;
};

// Días completos transcurridos desde el ingreso. null si no hay fecha utilizable.
export const diasDesdeIngreso = (p: any): number | null => {
  const ingreso = fechaIngresoPersona(p);
  if (!ingreso) return null;
  return Math.floor((hoyMidnight().getTime() - ingreso.getTime()) / 86_400_000);
};

// ¿La persona cuenta como "nueva" para la UI?
// Se respeta al backend cuando dice que NO, pero su "sí" se revalida contra la
// fecha real. Sin fecha no se puede acreditar la ventana → no es nueva.
export const esPersonaNueva = (p: any): boolean => {
  if (p?.es_nuevo === false) return false;
  const dias = diasDesdeIngreso(p);
  if (dias === null) return false;
  return dias >= 0 && dias <= DIAS_NUEVO;
};

// El badge fue fijado a mano en el backend (override que allá no expira).
export const tieneNuevoForzado = (p: any): boolean => p?.forzar_nuevo === true;
